import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useActivitySendController } from "@/components/conversations/activities/use-activity-send-controller";
import { useAvailableActivityItems } from "@/components/conversations/activities/use-available-activity-items";
import { useHubStore } from "@/components/hub/use-hub-store";
import type { AvailableActivity } from "@/lib/api/activities";
import { useAbility } from "@/lib/casl/ability-context";
import {
  useActionSuggestion,
  type UseActionSuggestionResult,
} from "./context/use-action-suggestion";
import { launchSuggestionAction } from "./launch-suggestion-action";
import type { UseActionAnalyticsResult } from "./use-action-analytics";
import {
  contentToolById,
  type ContentToolId,
} from "./tools/tool-config";
import { useContentToolGates } from "./tools/use-content-tool-gates";

interface ActionLaunchContextValue extends UseActionSuggestionResult {
  activities: AvailableActivity[];
  activitiesLoading: boolean;
  activitiesError: boolean;
  refetchActivities: () => void;
  selectActivity: (activity: AvailableActivity) => void;
  canShareContent: boolean;
  contentGates: Record<ContentToolId, boolean>;
  openContentCreate: (id: ContentToolId) => void;
  launchSuggestion: () => void;
}

const ActionLaunchContext = createContext<ActionLaunchContextValue | null>(
  null,
);

interface ActionLaunchProviderProps {
  menteeId: string | null;
  mentorshipId: string | null;
  teenName: string;
  analytics: UseActionAnalyticsResult;
  children: ReactNode;
}

export function ActionLaunchProvider({
  menteeId,
  mentorshipId,
  teenName,
  analytics,
  children,
}: ActionLaunchProviderProps) {
  const ability = useAbility();
  const canShareContent = ability.can("create", "shareable-entities");
  const contentGates = useContentToolGates();
  const openToView = useHubStore((s) => s.openToView);

  const suggestionState = useActionSuggestion(menteeId);
  const { items, isLoading, isError, refetch } = useAvailableActivityItems(
    mentorshipId ?? "",
  );
  const { selectActivity, dialogs } = useActivitySendController({
    mentorshipId: mentorshipId ?? "",
    teenName,
    sharedDeepThoughtsStore: false,
  });

  const openContentCreate = useCallback(
    (id: ContentToolId) => {
      if (!canShareContent || !contentGates[id]) return;
      analytics.trackToolSelect("content", id);
      openToView(contentToolById(id).createView);
    },
    [analytics, canShareContent, contentGates, openToView],
  );

  const notifyQaPlaceholder = useCallback(() => {
    window.alert(
      "This is forced UI. Use a mentorship with a real pending activity or Hub entity to test navigation.",
    );
  }, []);

  const launchSuggestion = useCallback(() => {
    const suggestion = suggestionState.suggestion;
    if (!suggestion) return;
    if (suggestionState.isForcedQaEntitySuggestion) {
      notifyQaPlaceholder();
      analytics.trackSuggestionAccept(suggestion.kind);
      return;
    }
    const launched = launchSuggestionAction({
      suggestion,
      openContentCreate,
      selectActivity,
      activities: items,
      openHubEntity: openToView,
    });
    if (launched) {
      analytics.trackSuggestionAccept(suggestion.kind);
    }
  }, [
    analytics,
    items,
    openContentCreate,
    openToView,
    notifyQaPlaceholder,
    selectActivity,
    suggestionState.isForcedQaEntitySuggestion,
    suggestionState.suggestion,
  ]);

  const value = useMemo<ActionLaunchContextValue>(
    () => ({
      ...suggestionState,
      activities: items,
      activitiesLoading: mentorshipId === null || isLoading,
      activitiesError: isError,
      refetchActivities: refetch,
      selectActivity,
      canShareContent,
      contentGates,
      openContentCreate,
      launchSuggestion,
    }),
    [
      suggestionState,
      items,
      mentorshipId,
      isLoading,
      isError,
      refetch,
      selectActivity,
      canShareContent,
      contentGates,
      openContentCreate,
      launchSuggestion,
    ],
  );

  return (
    <ActionLaunchContext.Provider value={value}>
      {children}
      {dialogs}
    </ActionLaunchContext.Provider>
  );
}

export function useActionLaunch(): ActionLaunchContextValue {
  const ctx = useContext(ActionLaunchContext);
  if (!ctx) {
    throw new Error("useActionLaunch must be used within ActionLaunchProvider");
  }
  return ctx;
}
